import { useTranslation } from '../../../shared/i18n';
import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, Select, Button, Alert, Tag, Typography, Space, Spin, Collapse, Table, List } from 'antd';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled,
  InfoCircleFilled,
  MedicineBoxOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';

const { Title, Text } = Typography;

const LEVEL = {
  error: { icon: <CloseCircleFilled style={{ color: '#ff4d4f' }} />, tag: 'error', label: '问题' },
  warn: { icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />, tag: 'warning', label: '注意' },
  info: { icon: <InfoCircleFilled style={{ color: '#1677ff' }} />, tag: 'blue', label: '提示' },
  ok: { icon: <CheckCircleFilled style={{ color: '#52c41a' }} />, tag: 'success', label: '正常' },
};

const SUMMARY = { error: 'error', warn: 'warning', ok: 'success' };

function FindingItem({ f }) {
  const { t } = useTranslation();
  const lv = LEVEL[f.level] || LEVEL.info;
  return (
    <List.Item style={{ alignItems: 'flex-start' }}>
      <Space align="start" size={12}>
        <span style={{ fontSize: 18, lineHeight: '22px' }}>{lv.icon}</span>
        <div>
          <Text strong>{f.title}</Text>
          <div><Text type="secondary">{f.cause}</Text></div>
          {f.fix && <div style={{ marginTop: 4 }}><Text>{t('Action: {{fix}}', { fix: f.fix })}</Text></div>}
        </div>
      </Space>
    </List.Item>
  );
}

const nodeColumns = [
  { title: '节点', dataIndex: 'name' },
  {
    title: 'HTTPS',
    render: (_, r) => (r.https_error
      ? <Tag color="error">{r.https_error}</Tag>
      : r.https_status !== undefined ? <Tag color={r.https_status >= 500 ? 'error' : 'default'}>{r.https_status}</Tag> : '-'),
  },
  {
    title: 'HTTP',
    render: (_, r) => (r.http_error ? <Tag color="error">{r.http_error}</Tag> : r.http_status ?? '-'),
  },
  { title: '耗时', dataIndex: 'https_ms', render: (v) => (v ? `${v}ms` : '-') },
  { title: '缓存', dataIndex: 'cache', render: (v) => v || '-' },
  {
    title: '证书到期',
    dataIndex: 'cert_expires',
    render: (v) => (v ? new Date(v * 1000).toLocaleDateString() : '-'),
  },
];

export default function Diagnostics() {
  const { t } = useTranslation();
  const { domainData } = useOutletContext();
  const [selectedHostname, setSelectedHostname] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const hostnames = domainData?.hostnames || [];
  const currentHostname = selectedHostname || hostnames[0]?.hostname;

  const handleRun = async () => {
    if (!currentHostname) return;
    setLoading(true);
    setResult(null);
    setError(null);
    const res = await cdnService.runDiagnostics({ hostname: currentHostname });
    if (res.success) setResult(res.data);
    else setError(res.error || t('Diagnostics failed. Please try again.'));
    setLoading(false);
  };

  const findings = result?.findings || [];
  const issues = findings.filter((f) => f.level !== 'ok');
  const oks = findings.filter((f) => f.level === 'ok');

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        <MedicineBoxOutlined style={{ marginRight: 8 }} />{t('Site diagnostics')}</Title>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            style={{ minWidth: 300 }}
            placeholder={t('Select a hostname')}
            value={currentHostname}
            onChange={setSelectedHostname}
            options={hostnames.map((h) => ({ label: h.hostname, value: h.hostname }))}
          />
          <Button type="primary" icon={<ThunderboltOutlined />} loading={loading} onClick={handleRun} disabled={!currentHostname}>{t('Start diagnostics')}</Button>
          <Text type="secondary">{t('Test DNS, each CDN node, and the origin, and check logs from the last 1 hour. Usually completes within 10 seconds.')}</Text>
        </Space>
      </Card>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip={t('Running diagnostics...')} />
        </div>
      )}

      {result && !loading && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert type={SUMMARY[result.summary.status] || 'info'} showIcon message={<Text strong>{result.summary.message}</Text>} />

          {issues.length > 0 && (
            <Card size="small" title={t('Action required')}>
              <List dataSource={issues} renderItem={(f) => <FindingItem f={f} />} />
            </Card>
          )}

          <Collapse
            items={[
              oks.length > 0 && {
                key: 'ok',
                label: t('Passed checks ({{length}})', { length: oks.length }),
                children: <List dataSource={oks} renderItem={(f) => <FindingItem f={f} />} />,
              },
              (result.nodes || []).length > 0 && {
                key: 'nodes',
                label: t('Node details ({{length}})', { length: result.nodes.length }),
                children: (
                  <Table size="small" rowKey="name" pagination={false} scroll={{ x: true }} columns={nodeColumns.map(column => ({ ...column, title: t(column.title) }))} dataSource={result.nodes} />
                ),
              },
            ].filter(Boolean)}
          />
        </Space>
      )}
    </div>
  );
}
